import { store } from "..";
import { ADD_NOTIFICATION } from "../store/types";
import {
	changeUserPassword,
	forgotPassword,
	getAccountFacilityData,
	getAllUsersByAccountData,
	getFacilityOptionData,
	getManageFacilitiesData,
	saveNewPassword,
	saveUser,
	updateFirstPassword,
	updateUser,
	updateUserProfile,
	userLogin,
	fetchUserEmailIntegrations,
	getIntegrationLoginURL,
	disconnectIntegration,
	updateUserPauseDateAPI,
	getAuth0State
} from "./api/user.api";
import { logErrorToSentry } from "../utilis/sentry-logger";
import { getApiErrorInfo } from "../utilis/api-error";

const getAccountFacility = async (filter) => {
	try {
		return await getAccountFacilityData(filter);
	} catch (e) {
		logErrorToSentry(e, {
			service: 'user-service',
			event: 'get_account_facility_error',
			extra: {
				filter,
				statusCode: e?.response?.status
			}
		});
	}
};

const getFacilityOption = async () => {
	try {
		return await getFacilityOptionData();
	} catch (e) {
		logErrorToSentry(e, {
			service: 'user-service',
			event: 'get_facility_option_error',
			extra: {
				statusCode: e?.response?.status
			}
		});
	}
};

const getManageFacilities = async (params) => {
	try {
		return await getManageFacilitiesData(params);
	} catch (e) {
		logErrorToSentry(e, {
			service: 'user-service',
			event: 'get_manage_facilities_error',
			extra: {
				params,
				statusCode: e?.response?.status
			}
		});
	}
};

const createUser = async (body) => {
	try {
		const res = await saveUser(body);
		if (res && res?._id) {
			store.dispatch({
				type: ADD_NOTIFICATION,
				payload: {
					type: "success",
					label: "User added successfully",
					id: "addUserError",
				},
			});
		}
		return res;
	} catch (e) {
		const { message } = getApiErrorInfo(e);
		logErrorToSentry(e, {
			service: 'user-service',
			event: 'create_user_error',
			extra: {
				body,
				statusCode: e?.response?.status,
				errorMessage: message
			}
		});
		store.dispatch({
			type: ADD_NOTIFICATION,
			payload: {
				type: "error",
				label: message || "Error while create new user.",
				id: "createErrorUser",
			},
		});
	}
};

const updateUserPauseDate = async (body) => {
	try {
		const res = await updateUserPauseDateAPI(body);
		if (res) {
			store.dispatch({
				type: ADD_NOTIFICATION,
				payload: {
					type: "success",
					label: "User saved successfully",
					id: "updateUserError",
				},
			});
		}
		return res;
	} catch (e) {
		const { message } = getApiErrorInfo(e);
		logErrorToSentry(e, {
			service: 'user-service',
			event: 'update_user_pause_date_error',
			extra: {
				body,
				statusCode: e?.response?.status,
				errorMessage: message
			}
		});
		store.dispatch({
			type: ADD_NOTIFICATION,
			payload: {
				type: "error",
				label: message || "Error while update new user.",
				id: "updateUserError",
			},
		});
	}
};
const updateOneUser = async (id, body) => {
	try {
		const res = await updateUser(id, body);
		if (res) {
			store.dispatch({
				type: ADD_NOTIFICATION,
				payload: {
					type: "success",
					label: "User saved successfully",
					id: "updateUserError",
				},
			});
		}
		return res;
	} catch (e) {
		const { message } = getApiErrorInfo(e);
		logErrorToSentry(e, {
			service: 'user-service',
			event: 'update_user_error',
			extra: {
				userId: id,
				body,
				statusCode: e?.response?.status,
				errorMessage: message
			}
		});
		store.dispatch({
			type: ADD_NOTIFICATION,
			payload: {
				type: "error",
				label: message || "Error while update new user.",
				id: "updateUserError",
			},
		});
	}
};

const saveUserProfile = async (body) => {
	try {
		const res = await updateUserProfile(body);
		if (res && res?.data) {
			store.dispatch({
				type: ADD_NOTIFICATION,
				payload: {
					type: "success",
					label: "User profile updated successfully",
					id: "updateUserError",
				},
			});
		}
		return res;
	} catch (e) {
		const { message } = getApiErrorInfo(e);
		logErrorToSentry(e, {
			service: 'user-service',
			event: 'save_user_profile_error',
			extra: {
				body,
				statusCode: e?.response?.status,
				errorMessage: message
			}
		});
		store.dispatch({
			type: ADD_NOTIFICATION,
			payload: {
				type: "error",
				label: message || "Error while update new user.",
				id: "updateUserError",
			},
		});
	}
};

const changePassword = async (id, body) => {
	try {
		const res = await changeUserPassword(id, body);
		if (res) {
			store.dispatch({
				type: ADD_NOTIFICATION,
				payload: {
					type: "success",
					label: "Password changed successfully.",
					id: "updateUserError",
				},
			});
		}
		return res;
	} catch (e) {
		const { message } = getApiErrorInfo(e);
		logErrorToSentry(e, {
			service: 'user-service',
			event: 'change_password_error',
			extra: {
				userId: id,
				statusCode: e?.response?.status,
				errorMessage: message
			}
		});
		store.dispatch({
			type: ADD_NOTIFICATION,
			payload: {
				type: "error",
				label: message || "Error while change password.",
				id: "updateUserError",
			},
		});
	}
};

const forgotPasswordLink = async (body) => {
	try {
		const res = await forgotPassword(body);
		if (res) {
			store.dispatch({
				type: ADD_NOTIFICATION,
				payload: {
					type: "success",
					label: `Reset password link sent successfully to your registered email ${body.email}.`,
					id: "updateUserError",
				},
			});
		}
		return res;
	} catch (e) {
		const { message } = getApiErrorInfo(e);
		logErrorToSentry(e, {
			service: 'user-service',
			event: 'forgot_password_link_error',
			extra: {
				email: body?.email,
				statusCode: e?.response?.status,
				errorMessage: message
			}
		});
		store.dispatch({
			type: ADD_NOTIFICATION,
			payload: {
				type: "error",
				label: message || "Error while change password.",
				id: "updateUserError",
			},
		});
	}
};

const resetUserPassword = async (body) => {
	try {
		const res = await saveNewPassword(body);
		if (res) {
			store.dispatch({
				type: ADD_NOTIFICATION,
				payload: {
					type: "success",
					label: "Your password updated successfully.",
					id: "updateUserError",
				},
			});
		}
		return res;
	} catch (e) {
		const { message } = getApiErrorInfo(e);
		logErrorToSentry(e, {
			service: 'user-service',
			event: 'reset_user_password_error',
			extra: {
				statusCode: e?.response?.status,
				errorMessage: message
			}
		});
		store.dispatch({
			type: ADD_NOTIFICATION,
			payload: {
				type: "error",
				label: message || "Error while update password.",
				id: "updateUserError",
			},
		});
	}
};

const updateFirstUserPassword = async (userId, body) => {
	try {
		const res = await updateFirstPassword(userId, body);
		if (res) {
			store.dispatch({
				type: ADD_NOTIFICATION,
				payload: {
					type: "success",
					label: "Your password updated successfully.",
					id: "updateUserError",
				},
			});
		}
		return res;
	} catch (e) {
		const { message } = getApiErrorInfo(e);
		logErrorToSentry(e, {
			service: 'user-service',
			event: 'update_first_user_password_error',
			extra: {
				userId,
				statusCode: e?.response?.status,
				errorMessage: message
			}
		});
		store.dispatch({
			type: ADD_NOTIFICATION,
			payload: {
				type: "error",
				label: message || "Error while update password.",
				id: "updateUserError",
			},
		});
	}
};

const authLogin = async (body) => {
	let attempts = 0;
	const maxAttempts = 5;
	while (attempts < maxAttempts) {
		try {
			const res = await userLogin(body);
			if (res) {
				store.dispatch({
					type: ADD_NOTIFICATION,
					payload: {
						type: "success",
						label: "User logged successfully.",
						id: "updateUserError",
					},
				});
				return res;
			}
		} catch (e) {
			const statusCode = e?.response?.status;
			const errorData = e?.response?.data;
			const errorMessage = errorData?.error?.message || errorData?.message;

			if (errorMessage === "Username or Password are not correct") {
				store.dispatch({
					type: ADD_NOTIFICATION,
					payload: {
						type: "error",
						label: errorMessage || "Error while login user",
						id: "updateUserError",
					},
				});
				return;
			}

			if (statusCode === 429) {
				const retryAfter = errorData?.retryAfter;
				const minutes = retryAfter ? Math.ceil(retryAfter / 60) : 15;
				store.dispatch({
					type: ADD_NOTIFICATION,
					payload: {
						type: "error",
						label: errorMessage || `Too many attempts. Please try again in ${minutes} minutes.`,
						id: "rateLimitError",
					},
				});
				throw e;
			}

			if (errorData?.requiresCaptcha) {
				store.dispatch({
					type: ADD_NOTIFICATION,
					payload: {
						type: "error",
						label: "Please complete the CAPTCHA verification.",
						id: "captchaRequired",
					},
				});
				throw e;
			}

			logErrorToSentry(e, {
				service: 'user-service',
				event: 'auth_login_error',
				extra: {
					attempt: attempts + 1,
					maxAttempts,
					statusCode,
					errorMessage
				}
			});

			store.dispatch({
				type: ADD_NOTIFICATION,
				payload: {
					type: "error",
					label: "Retrying login",
					id: "updateUserErrorRetry",
				},
			});

			attempts++;
			if (attempts === maxAttempts) {
				logErrorToSentry('Auth login: Maximum login attempts reached', {
					service: 'user-service',
					event: 'auth_login_max_attempts',
					extra: {
						maxAttempts,
						statusCode
					}
				});
				store.dispatch({
					type: ADD_NOTIFICATION,
					payload: {
						type: "error",
						label: "Maximum login attempts reached.",
						id: "updateUserErrorMaxAttempts",
					},
				});
				return;
			}
		}
	}
};

const getAllUsersByAccount = async (req) => {
	try {
		const res = await getAllUsersByAccountData(req);
		return res;
	} catch (e) {
		logErrorToSentry(e, {
			service: 'user-service',
			event: 'get_all_users_by_account_error',
			extra: {
				req,
				statusCode: e?.response?.status
			}
		});
	}
};

const fetchUserIntegrations = async () => {
	try {
		const res = await fetchUserEmailIntegrations();
		return res;
	} catch (e) {
		logErrorToSentry(e, {
			service: 'user-service',
			event: 'fetch_user_integrations_error',
			extra: {
				statusCode: e?.response?.status,
				errorMessage: e?.response?.data?.message
			}
		});
		return {
			microsoft: { connected: false, email: null },
			google: { connected: false, email: null },
		};
	}
};

const startEmailIntegrationLogin = async (provider, redirectAfter) => {
	try {
		const url = await getIntegrationLoginURL(provider, redirectAfter);
		if (url) {
			window.location.href = url;
		} else {
			logErrorToSentry('No login URL returned for email integration', {
				service: 'user-service',
				event: 'email_integration_no_url',
				extra: {
					provider,
					redirectAfter
				}
			});
		}
	} catch (error) {
		logErrorToSentry(error, {
			service: 'user-service',
			event: 'start_email_integration_login_error',
			extra: {
				provider,
				redirectAfter,
				statusCode: error?.response?.status
			}
		});
	}
};

const removeEmailIntegration = async (provider) => {
	await disconnectIntegration(provider);
	const { data } = await fetchUserEmailIntegrations();
	return data;
};

const fetchAuth0State = async (tenantId) => {
	try {
		const data = await getAuth0State(tenantId);
		return data?.state;
	} catch (e) {
		logErrorToSentry(e, {
			service: 'user-service',
			event: 'fetch_auth0_state_error',
			extra: {
				tenantId,
				statusCode: e?.response?.status
			}
		});
		return null;
	}
};

export {
	authLogin,
	changePassword,
	createUser,
	forgotPasswordLink,
	getAccountFacility,
	getFacilityOption,
	getManageFacilities,
	resetUserPassword,
	saveUserProfile,
	updateFirstUserPassword,
	updateOneUser,
	getAllUsersByAccount,
	fetchUserIntegrations,
	startEmailIntegrationLogin,
	removeEmailIntegration,
	updateUserPauseDate,
	fetchAuth0State
};

